<script>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue';
import caxios from '../../axios';
import { Modal } from 'bootstrap';
import { useAuthStore } from '@/stores/auth';
import { useTranslationStore } from '@/stores/translation';
import { authText } from '@/i18n/authText';

export default {
  name: 'ForgotPasswordOTP',
  props: {
    closeModalRef: {
      type: Object,
      default: null
    }
  },
  setup(props) {
    // Component state
    const currentStep = ref(1); // 1: Email input, 2: OTP verification, 3: New password
    const authStore = useAuthStore();
    const translationStore = useTranslationStore();
    const L = computed(() => authText(translationStore.currentLanguage));
    const otpModal = ref(null);
    const intro = ref('');   // why they're here when sign-up / sign-in sent them (claim an existing account)
    const closeBtn = ref(null);   // hidden data-bs-dismiss button
    const toLoginBtn = ref(null); // hidden link that swaps this modal for the login modal
    const loading = ref(false);

    // Email form data
    const emailForm = reactive({
      email: ''
    });
    // When the identifier was a PHONE, the server tells us (masked) where the code went.
    const maskedEmail = ref('');

    // OTP form data  
    const otpForm = reactive({
      otp_code: '',
      new_password: '',
      confirm_password: ''
    });

    // UI State
    const showPassword = ref(false);
    const showConfirmPassword = ref(false);
    
    // Timer for OTP countdown
    const timeRemaining = ref(15 * 60); // 15 minutes in seconds
    const timerInterval = ref(null);

    // Error handling
    const errors = ref({});
    const errorMessage = ref('');
    const successMessage = ref('');

    // Computed properties
    const formattedTime = computed(() => {
      const minutes = Math.floor(timeRemaining.value / 60);
      const seconds = timeRemaining.value % 60;
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    });

    // Resend becomes available 60s after the last send, not only when the full 15-minute
    // OTP window expires (a customer who never got the email was stuck for 15 minutes).
    const resendReady = computed(() => (15 * 60 - timeRemaining.value) >= 60);
    const resendWaitSecs = computed(() => Math.max(0, 60 - (15 * 60 - timeRemaining.value)));

    // Timer functions
    const startTimer = () => {
      clearTimer(); // Clear any existing timer
      timeRemaining.value = 15 * 60; // Reset to 15 minutes
      timerInterval.value = setInterval(() => {
        if (timeRemaining.value > 0) {
          timeRemaining.value--;
        } else {
          clearTimer();
        }
      }, 1000);
    };

    const clearTimer = () => {
      if (timerInterval.value) {
        clearInterval(timerInterval.value);
        timerInterval.value = null;
      }
    };

    // Validation functions — the identifier can be an EMAIL or an AU MOBILE number
    // (accounts created from phone orders may have no email yet).
    const validateEmail = () => {
      const tempErrors = {};
      const v = emailForm.email.trim();
      if (!v) {
        tempErrors.email = L.value.idRequired;
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phoneDigits = v.replace(/\D/g, '');
        const looksLikePhone = !v.includes('@') && phoneDigits.length >= 8 && phoneDigits.length <= 12;
        if (!emailRegex.test(v) && !looksLikePhone) {
          tempErrors.email = L.value.idInvalid;
        }
      }

      errors.value = tempErrors;
      return Object.keys(tempErrors).length === 0;
    };

    const validateOTP = () => {
      const tempErrors = {};

      if (!otpForm.otp_code.trim()) {
        tempErrors.otp_code = L.value.codeReq;
      } else if (!/^\d{6}$/.test(otpForm.otp_code.trim())) {
        tempErrors.otp_code = L.value.codeBad;
      }

      if (!otpForm.new_password) {
        tempErrors.new_password = L.value.pwReq;
      } else if (otpForm.new_password.length < 8) {
        tempErrors.new_password = L.value.pwShort;
      } else if (/^\d+$/.test(otpForm.new_password)) {
        tempErrors.new_password = L.value.pwNumeric;
      }

      if (!otpForm.confirm_password) {
        tempErrors.confirm_password = L.value.pwConfirmReq;
      } else if (otpForm.new_password !== otpForm.confirm_password) {
        tempErrors.confirm_password = L.value.pwMismatch;
      }

      errors.value = tempErrors;
      return Object.keys(tempErrors).length === 0;
    };

    // API functions
    const sendOTP = async () => {
      if (!validateEmail()) {
        return;
      }

      loading.value = true;
      errorMessage.value = '';
      successMessage.value = '';

      try {
        const response = await caxios.post('/forget-password-otp/', {
          email: emailForm.email.trim()
        });

        successMessage.value = response.data.message;
        // sent:false = the account has no email linked (phone-only) — show the guidance
        // and STAY on step 1; there is no code on its way anywhere.
        if (response.data.sent === false) {
          errorMessage.value = translationStore.currentLanguage === 'zh' ? L.value.phoneNoEmail : response.data.message;
          successMessage.value = '';
          return;
        }
        maskedEmail.value = response.data.masked_email || '';
        currentStep.value = 2;
        startTimer();

      } catch (error) {
        if (error.response && error.response.status === 429) {
          errorMessage.value = L.value.codeLimited;
        } else if (error.response && error.response.data) {
          if (error.response.data.errors) {
            errors.value = error.response.data.errors;
            const nf = errors.value.non_field_errors;
            if (nf) errorMessage.value = Array.isArray(nf) ? nf[0] : String(nf);
          } else {
            errorMessage.value = error.response.data.message || error.response.data.error || L.value.unexpected;
          }
        } else {
          errorMessage.value = L.value.network;
        }
      } finally {
        loading.value = false;
      }
    };

    const verifyOTP = async () => {
      if (!validateOTP()) {
        return;
      }

      loading.value = true;
      errorMessage.value = '';

      try {
        await caxios.post('/verify-otp/', {
          email: emailForm.email.trim(),
          otp_code: otpForm.otp_code.trim(),
          new_password: otpForm.new_password,
          confirm_password: otpForm.confirm_password
        });

        successMessage.value = L.value.resetDone;
        clearTimer();

        // Sign them straight in with the new password — they were usually mid-checkout.
        // If that fails for any reason, fall back to the login modal like before.
        let signedIn = false;
        try {
          await authStore.login({ email: emailForm.email.trim(), password: otpForm.new_password });
          signedIn = true;
        } catch (e) { /* fall back to the login screen */ }
        setTimeout(() => {
          resetForm();
          if (signedIn && closeBtn.value) closeBtn.value.click();
          else if (toLoginBtn.value) toLoginBtn.value.click();
          else if (closeBtn.value) closeBtn.value.click();
          else if (props.closeModalRef) props.closeModalRef.click();
        }, signedIn ? 700 : 1600);

      } catch (error) {
        if (error.response && error.response.status === 429) {
          errorMessage.value = L.value.codeLimited;
        } else if (error.response && error.response.data) {
          if (error.response.data.errors) {
            errors.value = error.response.data.errors;
            const nf = errors.value.non_field_errors;
            if (nf) errorMessage.value = Array.isArray(nf) ? nf[0] : String(nf);
          } else {
            errorMessage.value = error.response.data.error || L.value.unexpected;
          }
        } else {
          errorMessage.value = L.value.network;
        }
      } finally {
        loading.value = false;
      }
    };

    const resendOTP = async () => {
      loading.value = true;
      errorMessage.value = '';

      try {
        await caxios.post('/resend-otp/', {
          email: emailForm.email.trim()
        });

        successMessage.value = L.value.resentOk;
        startTimer();
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          successMessage.value = '';
        }, 3000);

      } catch (error) {
        if (error.response && error.response.status === 429) {
          errorMessage.value = L.value.codeLimited;
        } else if (error.response && error.response.data) {
          errorMessage.value = error.response.data.message || error.response.data.error || L.value.unexpected;
        } else {
          errorMessage.value = L.value.network;
        }
      } finally {
        loading.value = false;
      }
    };

    // Utility functions
    const resetForm = () => {
      currentStep.value = 1;
      emailForm.email = '';
      otpForm.otp_code = '';
      otpForm.new_password = '';
      otpForm.confirm_password = '';
      errors.value = {};
      errorMessage.value = '';
      successMessage.value = '';
      intro.value = '';
      maskedEmail.value = '';
      showPassword.value = false;
      showConfirmPassword.value = false;
      clearTimer();
    };

    const goBackToEmail = () => {
      currentStep.value = 1;
      errors.value = {};
      errorMessage.value = '';
      clearTimer();
    };

    const togglePasswordVisibility = (field) => {
      if (field === 'new_password') {
        showPassword.value = !showPassword.value;
      } else if (field === 'confirm_password') {
        showConfirmPassword.value = !showConfirmPassword.value;
      }
    };

    const clearError = (field) => {
      if (errors.value[field]) {
        delete errors.value[field];
      }
    };

    // Handle OTP input - only allow numbers and limit to 6 digits
    const handleOTPInput = (event) => {
      const value = event.target.value.replace(/\D/g, '').slice(0, 6);
      otpForm.otp_code = value;
    };

    // Sign-up / sign-in found an existing account without a password and a code is already on
    // its way: open straight at "enter code + choose a password".
    const onCodeStep = (ev) => {
      const d = (ev && ev.detail) || {};
      resetForm();
      emailForm.email = d.identifier || '';
      maskedEmail.value = d.masked || '';
      intro.value = d.intro || '';
      if (d.password) { otpForm.new_password = d.password; otpForm.confirm_password = d.password; }
      currentStep.value = 2;
      startTimer();
      if (otpModal.value) Modal.getOrCreateInstance(otpModal.value).show();
    };

    onMounted(() => {
      window.addEventListener('chds:code-step', onCodeStep);
    });

    onUnmounted(() => {
      window.removeEventListener('chds:code-step', onCodeStep);
      clearTimer();
    });

    return {
      // State
      L,
      intro,
      otpModal,
      currentStep,
      closeBtn,
      toLoginBtn,
      loading,
      emailForm,
      maskedEmail,
      otpForm,
      showPassword,
      showConfirmPassword,
      timeRemaining,
      errors,
      errorMessage,
      successMessage,
      
      // Computed
      formattedTime,
      resendReady,
      resendWaitSecs,
      
      // Methods
      sendOTP,
      verifyOTP,
      resendOTP,
      goBackToEmail,
      togglePasswordVisibility,
      clearError,
      handleOTPInput,
      resetForm
    };
  }
};
</script>

<template>
  <!-- Forgot Password OTP Modal -->
  <div
    class="modal modal-search-dish fade"
    id="forgotPasswordOTPModal"
    ref="otpModal"
    tabindex="-1"
    aria-labelledby="forgotPasswordOTPModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-scrollable modal-dialog-centered">
      <div class="modal-content">

        <!-- Modal Body -->
        <div class="modal-body text-center">
          <div class="login-content">
            <!-- Logo -->
            <div class="logo-wrap">
              <img src="@/assets/logo-wordmark-green.png" alt="CHDS 吃荤搭素" style="width: 150px; max-width: 70%; height: auto; object-fit: contain;" />
            </div>

            <!-- Step 1: Email Input -->
            <div v-if="currentStep === 1" class="email-step">
              <div class="login-text-wrap">
                <h4>{{ L.fpTitle }}</h4>
                <p>{{ L.fpSub }}</p>
              </div>

              <form @submit.prevent="sendOTP">
                <div class="form-wrap">
                  <!-- Email Input -->
                  <div class="mb-3 form-wrap-content">
                    <label for="email" class="form-label">{{ L.idLabel }}</label>
                    <div class="position-relative">
                      <div class="icon-email">
                        <i class="fa-solid fa-envelope"></i>
                      </div>
                      <input
                        type="text"
                        id="email"
                        v-model="emailForm.email"
                        @input="clearError('email')"
                        class="form-control"
                        :placeholder="L.idPh"
                        :class="{ 'is-invalid': errors.email }"
                      />
                      <div class="info-icon" @click="clearError('email')" v-if="errors.email">
                        <i class="fa-solid fa-circle-exclamation"></i>
                      </div>
                    </div>
                    <div class="invalid-feedback" v-if="errors.email">
                      {{ errors.email }}
                    </div>
                  </div>

                  <!-- Send OTP Button -->
                  <div class="button-wrap mb-3">
                    <button class="btn btn-primary w-100" type="submit" :disabled="loading">
                      <span v-if="loading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      <span v-else>{{ L.sendCode }}</span>
                    </button>
                  </div>

                  <!-- Error Message -->
                  <div v-if="errorMessage" class="alert alert-danger" role="alert">
                    {{ errorMessage }}
                  </div>

                  <!-- Success Message -->
                  <div v-if="successMessage" class="alert alert-success" role="alert">
                    {{ successMessage }}
                  </div>
                </div>

                <!-- Back to Login -->
                <div class="bottom-text">
                  <p>
                    {{ L.remember }}
                    <a data-bs-toggle="modal" data-bs-target="#loginModal">
                      <span>{{ L.backToLogin }}</span>
                    </a>
                  </p>
                </div>
              </form>
            </div>

            <!-- Step 2: OTP Verification -->
            <div v-if="currentStep === 2" class="otp-step">
              <div class="login-text-wrap">
                <h4>{{ L.vTitle }}</h4>
                <p v-if="intro" class="claim-intro">{{ intro }}</p>
                <p v-else>
                  {{ L.sentTo }} <strong>{{ maskedEmail || emailForm.email }}</strong>
                </p>
                <p class="timer-text">
                  {{ L.timeLeft }} <span class="countdown">{{ formattedTime }}</span>
                </p>
              </div>

              <form @submit.prevent="verifyOTP">
                <div class="form-wrap">
                  <!-- OTP Input -->
                  <div class="mb-3 form-wrap-content">
                    <label for="otp" class="form-label">{{ L.codeLabel }}</label>
                    <div class="position-relative">
                      <div class="icon-email">
                        <i class="fa-solid fa-key"></i>
                      </div>
                      <input
                        type="text"
                        id="otp"
                        v-model="otpForm.otp_code"
                        @input="handleOTPInput($event); clearError('otp_code')"
                        class="form-control text-center letter-spacing-wide"
                        :placeholder="L.codePh" inputmode="numeric" autocomplete="one-time-code"
                        maxlength="6"
                        :class="{ 'is-invalid': errors.otp_code }"
                      />
                    </div>
                    <div class="invalid-feedback" v-if="errors.otp_code">
                      {{ errors.otp_code }}
                    </div>
                  </div>

                  <!-- New Password Input -->
                  <div class="mb-3 form-wrap-content">
                    <label for="new_password" class="form-label">{{ L.newPw }}</label>
                    <div class="position-relative">
                      <div class="icon-email">
                        <i class="fa-solid fa-lock"></i>
                      </div>
                      <input
                        :type="showPassword ? 'text' : 'password'"
                        id="new_password"
                        v-model="otpForm.new_password"
                        @input="clearError('new_password')"
                        class="form-control"
                        :placeholder="L.newPwPh" autocomplete="new-password"
                        :class="{ 'is-invalid': errors.new_password }"
                      />
                      <div class="info-icon" @click="togglePasswordVisibility('new_password')">
                        <i :class="showPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
                      </div>
                    </div>
                    <div class="invalid-feedback" v-if="errors.new_password">
                      {{ errors.new_password }}
                    </div>
                  </div>

                  <!-- Confirm Password Input -->
                  <div class="mb-3 form-wrap-content">
                    <label for="confirm_password" class="form-label">{{ L.confirmPw }}</label>
                    <div class="position-relative">
                      <div class="icon-email">
                        <i class="fa-solid fa-lock"></i>
                      </div>
                      <input
                        :type="showConfirmPassword ? 'text' : 'password'"
                        id="confirm_password"
                        v-model="otpForm.confirm_password"
                        @input="clearError('confirm_password')"
                        class="form-control"
                        :placeholder="L.confirmPwPh" autocomplete="new-password"
                        :class="{ 'is-invalid': errors.confirm_password }"
                      />
                      <div class="info-icon" @click="togglePasswordVisibility('confirm_password')">
                        <i :class="showConfirmPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
                      </div>
                    </div>
                    <div class="invalid-feedback" v-if="errors.confirm_password">
                      {{ errors.confirm_password }}
                    </div>
                  </div>

                  <!-- Reset Password Button -->
                  <div class="button-wrap mb-3">
                    <button class="btn btn-primary w-100" type="submit" :disabled="loading">
                      <span v-if="loading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      <span v-else>{{ L.setPwBtn }}</span>
                    </button>
                  </div>

                  <!-- Resend OTP Button -->
                  <div class="button-wrap mb-3">
                    <button 
                      type="button" 
                      class="btn btn-outline-secondary w-100" 
                      @click="resendOTP"
                      :disabled="loading || !resendReady"
                    >
                      <span v-if="loading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      <span v-else>
                        {{ resendReady ? L.resend : L.resendIn(resendWaitSecs) }}
                      </span>
                    </button>
                  </div>

                  <!-- Error Message -->
                  <div v-if="errorMessage" class="alert alert-danger" role="alert">
                    {{ errorMessage }}
                  </div>

                  <!-- Success Message -->
                  <div v-if="successMessage" class="alert alert-success" role="alert">
                    {{ successMessage }}
                  </div>
                </div>

                <!-- Back to Email -->
                <div class="bottom-text">
                  <p>
                    {{ L.wrongId }}
                    <a href="#" @click.prevent="goBackToEmail">
                      <span>{{ L.changeId }}</span>
                    </a>
                  </p>
                </div>
              </form>
            </div>

            <!-- Close button (hidden) -->
            <button 
              type="button" 
              ref="closeBtn" 
              class="d-none" 
              data-bs-dismiss="modal" 
              aria-label="Close"
            ></button>
            <a ref="toLoginBtn" class="d-none" data-bs-toggle="modal" data-bs-target="#loginModal" aria-hidden="true">login</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.claim-intro { font-size: 14px; line-height: 1.5; color: #2f4a25; background: #f3f8e6; border-radius: 10px; padding: 10px 12px; text-align: left; }
/* Component-specific styles */

.invalid-feedback {
  display: block;
}

.info-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #6c757d;
}

.info-icon:hover {
  color: #495057;
}

.form-control.is-invalid {
  border-color: #dc3545;
}

.bottom-text p {
  margin-top: 1rem;
}

.bottom-text a {
  color: #0d6efd;
  text-decoration: none;
}

.bottom-text a:hover {
  text-decoration: underline;
}

.timer-text {
  font-size: 0.9rem;
  color: #6c757d;
}

.countdown {
  font-weight: bold;
  color: #dc3545;
}

.letter-spacing-wide {
  letter-spacing: 0.3em;
  font-size: 1.2rem;
  font-weight: bold;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.alert {
  margin-top: 1rem;
  font-size: 0.9rem;
}

.logo-wrap img {
  max-width: 120px;
  margin-bottom: 1rem;
}

.form-wrap-content {
  text-align: left;
}

.form-label {
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.icon-email {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  z-index: 3;
}

.form-control {
  padding-left: 40px;
}

.btn-outline-secondary {
  border-color: #6c757d;
  color: #6c757d;
}

.btn-outline-secondary:hover {
  background-color: #6c757d;
  color: white;
}

.modal-content {
  border-radius: 10px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

.login-text-wrap h4 {
  margin-bottom: 0.5rem;
  color: #333;
}

.login-text-wrap p {
  color: #6c757d;
  margin-bottom: 1rem;
}
</style> 